import clsx from "clsx";
import React, { forwardRef } from "react";
import NumberFormat from "react-number-format";
import { useUniqueWcagValues } from "common-react";
import { FieldErrorMessage } from "../FieldErrorMessage/FieldErrorMessage";
import { TextField } from "../TextField/TextField";
import { NumberInputPropsModel } from "./tools/NumberInput.model";

import useStyles from "./NumberInput.style";

export const NumberInput = forwardRef<
  HTMLInputElement | null,
  NumberInputPropsModel
>(
  (
    {
      name,
      value,
      onValueChange,
      id,
      adornment,
      adornmentPosition = 'end',
      decimalScale,
      fixedDecimalScale = true,
      placeholder,
      className,
      onFocus,
      onBlur,
      disabled = false,
      label,
      allowNegative = false,
      isAllowed,
      onKeyDown,
      ariaRequiredId,
      ariaDescribedBy,
      required = false,
      error = false,
      errorMessage,
      size,
      testId = "number-input-wrapper",
      autoTestId,
    },
    ref
  ) => {
    const c = useStyles({});
    const { getUniqueValue } = useUniqueWcagValues();
    const uniqueId = getUniqueValue(name || "");
    const uniqueErrorId = getUniqueValue(`${name || uniqueId}-error`);
    const errorId = error ? (ariaRequiredId || uniqueErrorId) : undefined;
    const describedBy = [errorId, ariaDescribedBy].filter(Boolean).join(" ") || undefined;

    return (
      <>
        <NumberFormat
          label={label}
          name={name}
          value={value}
          data-testid={testId}
          data-autotestid={autoTestId}
          className={clsx(c.root, className)}
          customInput={TextField}
          isAllowed={isAllowed}
          hasError={error}
          errorId={errorId}
          id={id || uniqueId}
          startAdornment={adornmentPosition === 'start' && adornment ? adornment : undefined}
          endAdornment={adornmentPosition === 'end' && adornment ? adornment : undefined}
          disabled={disabled}
          InputProps={{
            disabled,
          }}
          inputProps={{ "aria-describedby": describedBy }}
          size={size}
          // @ts-ignore - react-number-format ref type incompatibility
          inputRef={ref}
          inputMode="numeric"
          thousandSeparator=" "
          decimalSeparator=","
          decimalScale={decimalScale}
          fixedDecimalScale={fixedDecimalScale}
          placeholder={placeholder}
          onValueChange={values => onValueChange(values.floatValue)}
          onFocus={onFocus}
          onBlur={onBlur}
          required={required}
          onKeyDown={onKeyDown}
          allowNegative={allowNegative}
        />
        {error && errorMessage && (
          <FieldErrorMessage id={ariaRequiredId || uniqueErrorId}>
            {errorMessage}
          </FieldErrorMessage>
        )}
      </>
    );
  }
);

NumberInput.displayName = "NumberInput";
